import { createSelector } from 'reselect';
import { RootState } from '@state/types';
import { groupByDate } from '../../utils/taskUtils';
import { PileStateOptions } from 'shovel-lib/types';
import { initialUserSettings } from '../settings/reducers';
import { CalendarState } from '../calendar/types';
import { getType } from 'typesafe-actions';
import { calculateCushion, getAllToDoTasks, getCompletedTasks, getWithinStartDateTasks } from './actions';
import { PileSections, PileState, SortOption, TaskForVirtualization, VirtualizationSection } from './types';
import { extractActionNameWithoutSuffix } from '../common/utils';
import t from '../../i18n/t';
import { isFilterActive } from '../../components/pile/components/utils';
import { cardDates } from '../../utils/dayLabel';

const getPileFilterState = (state: RootState) => {
  const { filter, search, sort } = state.pile;
  return {
    filter,
    courses: (state.calendar as CalendarState).courses,
    activities: (state.calendar as CalendarState).activities,
    search,
    sort,
    activeTab: getActivePileTab(state)
  };
};

const getActivePileTab = ({ settings }: RootState) => (settings.userSettings || initialUserSettings).pileState;

const getAllTasks = (state: RootState) => {
  const noDueDate = state.pile[PileSections.NO_DUE_DATE];
  return Object.values(state.calendar.datesContainingTasks || [])
    .reduce((acc, events) => [...acc, ...events], [])
    .concat(noDueDate.tasks);
};

const getAllToDoState = ({ pile }: RootState) => {
  const { content, last, totalPages, page, totalElements } = pile.allToDo;
  return {
    last,
    totalPages,
    page,
    totalElements,
    loadedElements: content.length
  };
};

const NO_DUE_DATE_CARD_HEIGHT = 82;
const NO_START_AHEAD_CARD_HEIGHT = 84;
const FULL_CARD_HEIGHT = 99;
const EXPANDABLE_SECTION_COLLAPSED_HEIGHT = 52;
const EXPANDABLE_SECTION_EXPANDED_HEIGHT = 41;
const NON_EXPANDABLE_SECTION_TITLE_HEIGHT = 29;
const NO_LABEL_SECTION_TITLE_HEIGHT = 11;
const PADDING_AND_BORDER_BOTTOM = 21;
const MARK_ALL_COMPLETE_BUTTON_HEIGHT = 26;
const CREATED_AT_TEXT_HEIGHT = 16;

// A card has its Start line only while that day is still ahead (utils/dayLabel), and its row is that much taller:
// rows set by the start setting alone left gaps in the section's border under the cards that had dropped the line.
const cardHeight = (task: any) =>
  cardDates(task.due, task.daysToStartAhead).start ? FULL_CARD_HEIGHT : NO_START_AHEAD_CARD_HEIGHT;

const pileRows = (pile: PileState, activeTab: PileStateOptions) => {
  const { completed, sort } = pile;
  // Without the tasks just completed from their cards, so counts, spacing and section ends stay right.
  const hidden = new Set(pile.hiddenTaskIds || []);
  const shown = (tasks: any[] = []): any[] => (hidden.size ? tasks.filter(task => !hidden.has(task.taskId)) : tasks);
  const allToDo = { ...pile.allToDo, content: shown(pile.allToDo.content) };
  const withinStartDate = { ...pile.withinStartDate, tasks: shown(pile.withinStartDate.tasks) };
  const recentlyCreated = {
    ...pile[PileSections.RECENTLY_CREATED],
    tasks: shown(pile[PileSections.RECENTLY_CREATED].tasks)
  };
  const overdue = { ...pile[PileSections.OVERDUE], tasks: shown(pile[PileSections.OVERDUE].tasks) };
  const noDueDate = { ...pile[PileSections.NO_DUE_DATE], tasks: shown(pile[PileSections.NO_DUE_DATE].tasks) };
  const isCompleted = activeTab === PileStateOptions.COMPLETED;
  const tasks: (TaskForVirtualization | VirtualizationSection)[] = [];

  if (!isCompleted) {
    if (recentlyCreated.tasks.length > 0 && !recentlyCreated.expanded) {
      tasks.push({
        isLast: true,
        height: EXPANDABLE_SECTION_COLLAPSED_HEIGHT,
        section: PileSections.RECENTLY_CREATED,
        size: recentlyCreated.tasks.length,
        expanded: recentlyCreated.expanded
      });
    } else {
      recentlyCreated.tasks.forEach((task, i) => {
        if (i === 0) {
          tasks.push({
            height: EXPANDABLE_SECTION_EXPANDED_HEIGHT,
            section: PileSections.RECENTLY_CREATED,
            size: recentlyCreated.tasks.length,
            expanded: recentlyCreated.expanded
          });
        }
        let height = !task.due ? NO_DUE_DATE_CARD_HEIGHT : cardHeight(task);
        height += CREATED_AT_TEXT_HEIGHT;
        const isLast = i === recentlyCreated.tasks.length - 1;
        if (isLast) {
          height += PADDING_AND_BORDER_BOTTOM;
        }
        tasks.push({ isLast, task, height, section: PileSections.RECENTLY_CREATED });
      });
    }

    if (overdue.tasks.length > 0 && !overdue.expanded) {
      tasks.push({
        isLast: true,
        height: EXPANDABLE_SECTION_COLLAPSED_HEIGHT,
        section: PileSections.OVERDUE,
        size: overdue.tasks.length,
        expanded: overdue.expanded
      });
    } else {
      overdue.tasks.forEach((task, i) => {
        if (i === 0) {
          tasks.push({
            height: EXPANDABLE_SECTION_EXPANDED_HEIGHT,
            section: PileSections.OVERDUE,
            size: overdue.tasks.length,
            expanded: overdue.expanded
          });
        }
        let height = cardHeight(task);
        const isLast = i === overdue.tasks.length - 1;
        if (isLast) {
          height += PADDING_AND_BORDER_BOTTOM + MARK_ALL_COMPLETE_BUTTON_HEIGHT;
        }
        tasks.push({ isLast, task, height, section: PileSections.OVERDUE });
      });
    }

    if (noDueDate.tasks.length > 0 && !noDueDate.expanded) {
      tasks.push({
        isLast: true,
        height: EXPANDABLE_SECTION_COLLAPSED_HEIGHT,
        section: PileSections.NO_DUE_DATE,
        size: noDueDate.tasks.length,
        expanded: noDueDate.expanded
      });
    } else {
      noDueDate.tasks.forEach((task, i) => {
        if (i === 0) {
          tasks.push({
            height: EXPANDABLE_SECTION_EXPANDED_HEIGHT,
            section: PileSections.NO_DUE_DATE,
            size: noDueDate.tasks.length,
            expanded: noDueDate.expanded
          });
        }
        let height = NO_DUE_DATE_CARD_HEIGHT;
        const isLast = i === noDueDate.tasks.length - 1;
        if (isLast) {
          height += PADDING_AND_BORDER_BOTTOM;
        }
        tasks.push({ isLast, task, height, section: PileSections.NO_DUE_DATE });
      });
    }
  }

  let content: { [key: string]: any[] } = {};
  const sortByDueDate = (isCompleted ? sort.completed : sort.todo).sortField === SortOption.DUE_DATE;
  switch (activeTab) {
    case PileStateOptions.UPCOMING:
      content = sortByDueDate ? groupByDate(withinStartDate.tasks) : { '': withinStartDate.tasks };
      break;
    case PileStateOptions.ACTIVE:
      content = sortByDueDate ? groupByDate(allToDo.content) : { '': allToDo.content };
      break;
    case PileStateOptions.COMPLETED:
      content = sortByDueDate ? groupByDate(completed.tasks) : { '': completed.tasks };
      break;
    default:
    // invalid case
  }

  Object.entries(content).forEach(([key, sectionTasks]) => {
    sectionTasks.forEach((task, i) => {
      if (i === 0) {
        tasks.push({
          height: sortByDueDate ? NON_EXPANDABLE_SECTION_TITLE_HEIGHT : NO_LABEL_SECTION_TITLE_HEIGHT,
          section: key,
          isCompleted
        });
      }
      let height = isCompleted ? NO_START_AHEAD_CARD_HEIGHT : cardHeight(task);
      const isLast = i === sectionTasks.length - 1;
      if (isLast) {
        height += PADDING_AND_BORDER_BOTTOM;
      }
      tasks.push({ isLast, task, height, section: key, isCompleted });
    });
  });

  return { allToDo, overdue, tasks, selectedSectionCount: Object.keys(content).length };
};

/**
 * Each to-do task's cushion as the sidebar has it once the Cushion was calculated, by task id; a task without one is
 * missing. For the calendar's task popups, whose copies of the tasks come with a cushion of 0 that was never worked out.
 */
export const getPileCushions = createSelector(
  [(state: RootState) => state.pile.allToDo.content, (state: RootState) => state.pile.withinStartDate.tasks],
  (allToDo, withinStartDate) => {
    const cushions: Record<number, number> = {};
    [...allToDo, ...withinStartDate].forEach((task: any) => {
      if (task.cushion != null) cushions[task.taskId] = task.cushion;
    });
    return cushions;
  }
);

/**
 * Worked out again only when the sidebar's own state or its tab changes. The store changes many times while one task
 * saves - the lists' loaders, the cushion, the calendar - and rows made anew each time drew every card again each time.
 */
const getPileRows = createSelector([(state: RootState) => state.pile, getActivePileTab], pileRows);

const getPileState = (state: RootState) => {
  const { filter, search, sort } = state.pile;
  const activeTab = getActivePileTab(state);
  let emptyMessage: string = '';
  let loading: boolean = false;
  const filterOrSearchActive = !!search || isFilterActive(filter, activeTab);
  switch (activeTab) {
    case PileStateOptions.UPCOMING:
      emptyMessage = filterOrSearchActive
        ? t.WITHIN_START_DATE_EMPTY_MESSAGE_WITH_FILTER
        : t.WITHIN_START_DATE_EMPTY_MESSAGE;
      loading = state.common.loaders[extractActionNameWithoutSuffix(getWithinStartDateTasks.request)];
      break;
    case PileStateOptions.ACTIVE:
      emptyMessage = filterOrSearchActive ? t.ALL_TO_DO_EMPTY_MESSAGE_WITH_FILTER : t.ALL_TO_DO_EMPTY_MESSAGE;
      loading = state.common.loaders[extractActionNameWithoutSuffix(getAllToDoTasks.request)];
      break;
    case PileStateOptions.COMPLETED:
      emptyMessage = filterOrSearchActive ? t.COMPLETED_EMPTY_MESSAGE_WITH_FILTER : t.COMPLETED_EMPTY_MESSAGE;
      loading = state.common.loaders[extractActionNameWithoutSuffix(getCompletedTasks.request)];
      break;
    default:
    // invalid case
  }

  return { filter, search, sort, activeTab, ...getPileRows(state), loading, emptyMessage };
};

const getDisplayedActiveTasks = state => {
  const activeTab = getActivePileTab(state);
  return activeTab === PileStateOptions.ACTIVE ? state.pile.allToDo.content : state.pile.withinStartDate.tasks;
};

const getMoveToDoDialogState = ({ pile }: RootState) => {
  return pile.moveToDoDialog;
};

const isCalculatingCushion = ({ common }: RootState) => {
  const calculateCushionAction = getType(calculateCushion.request).split('_REQUEST')[0];
  return common.loaders[calculateCushionAction];
};

export {
  getPileFilterState,
  getActivePileTab,
  getAllTasks,
  getAllToDoState,
  getPileState,
  getMoveToDoDialogState,
  isCalculatingCushion,
  getDisplayedActiveTasks
};

/**
 * A to-do task as the sidebar last had it, wherever it is listed, or undefined when no loaded list has it. Read on the
 * spot rather than subscribed to: the calendar asks it once, as a block is marked used.
 */
export const findPileTask = ({ pile }: RootState, taskId: number) =>
  [
    pile.allToDo.content,
    pile.withinStartDate.tasks,
    pile[PileSections.OVERDUE].tasks,
    pile[PileSections.NO_DUE_DATE].tasks,
    pile[PileSections.RECENTLY_CREATED].tasks
  ]
    .flatMap(tasks => (tasks || []) as any[])
    .find(task => task.taskId === taskId) as { timeNeed: number; timePlanned: number } | undefined;
